import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ISelect } from '@tecalisdev/infinity';
import { environment } from '../../../environments/environment';
import { LanguageService } from '../../core/services/language.service';
import { IMasterCompanyElement } from '../../shared/models/master.models';
import { MasterService } from '../../shared/services/master.service';
import {
	AuthChannel,
	DEMAND_LEVEL,
	IWorkflowsCreatePayload,
	TwoFactorType,
} from '../models/workflows.models';
import {
	CONNECTION_TYPE,
	EVALUATION_TYPE,
	IChooseOperation,
	IConnectionOperation,
	IDocumentsDocs,
	IDocumentsOperation,
	IFinishOperation,
	IFormOperation,
	IFormOperationStep,
	IOcrAllowedDocs,
	IOcrOperation,
	IOperation,
	IPolOperation,
	IPolSetting,
	OPERATION_TYPE,
} from '../../operations/models/operations.models';
import {
	hasWorkflowLocaleText,
	serializeOptionalWorkflowLocaleForApi,
	serializeWorkflowLocaleFormValue,
} from '../utils/workflow-locale-text.util';
import { serializeFormStepToApi } from '../utils/workflow-form.util';
import {
	isOcrSelectFlagTrue,
	readOcrFormValue,
	readOcrSelectId,
	readOperationControlValue,
	readOperationSelectId,
	resolveSelectIdOrPlainValue,
} from '../utils/workflow-form-control.util';
import {
	buildAttorneyManualFallbackOrUndefined,
} from '../utils/workflow-attorney-powers.util';
import {
	normalizeOptionalDocNumber,
	normalizeDocsMatchData,
	partitionDocumentRowsForPayload,
	serializeDocumentMethodForApi,
} from '../utils/workflow-documents.util';
import { normalizeOptionalFinishUrl } from '../utils/workflow-finish.util';
import { serializeHomePolicyChecks } from '../utils/workflow-home.util';
import {
	buildOcrManualFallbackOrUndefined,
	OCR_FRAUD_CHECK_KEYS,
	mapOcrMultiSelectIds,
	ocrObfuscationFieldsFromSelected,
	resolveOcrSettingTriesAndTime,
} from '../utils/workflow-ocr.util';
import {
	buildRetentionApiFields,
	RetentionDurationApiValue,
} from '../../shared/utils/retention-duration.util';
import { WorkflowsHelperService } from './workflows-helper.service';
import {
	buildWorkflowValidationEmailsPayload,
	buildWorkflowValidationTypePayload,
} from '../utils/workflow-validation.util';

type DelayedWarningVisibility = 'start_and_resume' | 'resume_only';
type DelayedWarningMessageType = 'default' | 'custom';

/**
 * Host del editor para montar el payload de create/update (NIF-530).
 * El componente aporta form + getters derivados (docs/conexiones/POL) y helpers OCR de país.
 */
export interface WorkflowFormPayloadHost {
	/** Form reactivo del editor. */
	form: FormGroup;
	/** UUID del workflow en edición (`undefined` en alta) — afecta omit de retention null. */
	id: string | undefined;
	/** Pasos FORM (`operations.FORM.steps`). */
	get formStepsArray(): FormArray;
	/** Compañía seleccionada (afecta fallback manual / validation). */
	company?: IMasterCompanyElement;
	/** Docs del FormArray DOCUMENTS listos para serializar. */
	get docsAvailables(): IDocumentsDocs[];
	/** Tipos de conexión de evaluación financiera seleccionados. */
	get connectionsAvailables(): CONNECTION_TYPE[];
	/** Setting POL ya sanitizado desde el form (el componente lo construye). */
	get polSetting(): IPolSetting;
	/** Si el check de fraude OCR está activo en UI para esa clave. */
	availableCheckFraud(type: string): boolean;
	/** Lista de países allowed según tipo DNI/pasaporte del form. */
	getAllowedDocs(doc: 'dni' | 'passport'): Array<string> | null;
	/** Lista de países not_allowed según tipo DNI/pasaporte del form. */
	getNotAllowedDocs(doc: 'dni' | 'passport'): any;
	/** País compañía = España (ISO-3 esp) — condiciona envío de Schengen. */
	isCompanyCountrySpain(): boolean;
	/** UUIDs del catálogo `errors-numbers` (para serializar “todos seleccionados” como `errors: []`). */
	getValidationErrorOptionIds(): string[];
}

/**
 * Payload create/update del editor de workflows (NIF-530).
 *
 * Abrir cuando: montar el body de POST/PUT a partir del FormGroup.
 * Entrada: `buildCreatePayload(host)`.
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormPayloadService {
	private langSvc = inject(LanguageService);
	private workflowsHelper = inject(WorkflowsHelperService);
	private masterService = inject(MasterService);

	/**
	 * Orquestador: acumula operaciones habilitadas + sección general.
	 * La lógica por operación vive en `append*IfEnabled` / `buildGeneralConfigSection`.
	 */
	public buildCreatePayload(host: WorkflowFormPayloadHost): IWorkflowsCreatePayload {
		const allowedLocales = this.langSvc.getAllowedLanguages();
		let operations: IOperation[] = [];

		this.appendHomeOperationIfEnabled(host, operations, allowedLocales);
		this.appendFormOperationIfEnabled(host, operations, allowedLocales);
		this.appendOcrOperationIfEnabled(host, operations);
		this.appendPolOperationIfEnabled(host, operations);
		this.appendAttorneyPowersOperationIfEnabled(host, operations);
		this.appendDocumentsAndConnectionsIfEnabled(host, operations);
		this.appendFinishOperationIfEnabled(host, operations, allowedLocales);

		operations = this.orderOperationsForDemoFlow(operations);

		return {
			...this.buildGeneralConfigSection(host, allowedLocales),
			operations,
		} as IWorkflowsCreatePayload;
	}

	/** Si HOME está disponible, añade la operación con description/policy_checks serializados. */
	private appendHomeOperationIfEnabled(
		host: WorkflowFormPayloadHost,
		operations: IOperation[],
		allowedLocales: readonly string[],
	): void {
		if (!readOperationControlValue(host.form, OPERATION_TYPE.HOME, 'available')) {
			return;
		}
		const policyChecks = readOperationControlValue(host.form, OPERATION_TYPE.HOME, 'policy_checks') as
			| Array<{ uuid?: string }>
			| undefined;
		operations.push({
			type: OPERATION_TYPE.HOME,
			config: {
				tecalis_policy_check: readOperationControlValue(
					host.form,
					OPERATION_TYPE.HOME,
					'tecalis_policy_check',
				) as boolean,
				description: serializeWorkflowLocaleFormValue(
					readOperationControlValue(host.form, OPERATION_TYPE.HOME, 'description'),
					allowedLocales,
				) as any,
				policy_checks: serializeHomePolicyChecks(policyChecks) as any,
			},
			uuid: readOperationControlValue(host.form, OPERATION_TYPE.HOME, 'uuid') as string,
		});
	}

	/** Si FORM está disponible, serializa steps vía contract util + helper. */
	private appendFormOperationIfEnabled(
		host: WorkflowFormPayloadHost,
		operations: IOperation[],
		allowedLocales: readonly string[],
	): void {
		if (!readOperationControlValue(host.form, OPERATION_TYPE.FORM, 'available')) {
			return;
		}
		const steps = host.formStepsArray.getRawValue() as Array<Record<string, unknown>>;
		const autofillRaw = readOperationControlValue(host.form, OPERATION_TYPE.FORM, 'autofill_from_ocr');
		operations.push({
			type: OPERATION_TYPE.FORM,
			config: {
				tutorial: readOperationControlValue(host.form, OPERATION_TYPE.FORM, 'tutorial'),
				autofill_from_ocr: autofillRaw === true,
				steps: steps.map((step) =>
					serializeFormStepToApi(step, allowedLocales, (field) =>
						this.workflowsHelper.serializeFormField(field),
					),
				) as unknown as IFormOperationStep[],
			},
			uuid: readOperationControlValue(host.form, OPERATION_TYPE.FORM, 'uuid'),
		} as IFormOperation);
	}

	/**
	 * Si OCR está disponible, monta config + fraud_scoring + fallback.
	 * Fraud anidado: ver `buildOcrFraudScoringPayload` (abrir ese método si tocas fraude OCR).
	 */
	private appendOcrOperationIfEnabled(host: WorkflowFormPayloadHost, operations: IOperation[]): void {
		if (!readOcrFormValue(host.form, 'available')) {
			return;
		}

		const fraudType = readOcrSelectId(host.form, 'fraud_scoring.type');
		const isRecommendedDemand = fraudType === DEMAND_LEVEL.RECOMMENDED;
		const settingTriesAndTime = resolveOcrSettingTriesAndTime(
			isRecommendedDemand,
			readOcrFormValue(host.form, 'setting.total_tries'),
			readOcrFormValue(host.form, 'setting.side_max_time'),
		);

		operations.push({
			type: OPERATION_TYPE.OCR,
			config: {
				mode: readOcrSelectId(host.form, 'mode'),
				tutorial: readOcrFormValue(host.form, 'tutorial'),
				ocr_allowed_expiration: Number(readOcrFormValue(host.form, 'fraud_scoring.ocr_allowed_expiration')),
				validate_address: readOcrFormValue(host.form, 'fraud_scoring.validate_address'),
				validate_team_number: readOcrFormValue(host.form, 'fraud_scoring.validate_team_number'),
				fraud_scoring: this.buildOcrFraudScoringPayload(host),
				match_data: readOcrFormValue(host.form, 'match_data'),
				setting: {
					// Nivel RECOMMENDED fuerza tries/tiempo del preset; si no, valores del form.
					total_tries: settingTriesAndTime.total_tries,
					side_max_time: settingTriesAndTime.side_max_time,
					show_guide: readOcrFormValue(host.form, 'animations'),
					total_request: readOcrFormValue(host.form, 'setting.total_request'),
				},
				...this.buildOcrAllowedDocsPayload(host),
				required_check_nie: readOcrFormValue(host.form, 'required_check_nie'),
				scan_barcode: readOcrFormValue(host.form, 'scan_barcode'),
				obfuscation: this.buildOcrObfuscationPayload(host),
				document_watermark: Boolean(readOcrFormValue(host.form, 'document_watermark')),
				// Solo España: si no se envía, el back/BD aplica default true
				...(host.isCompanyCountrySpain()
					? {
							is_schengen_unlimited: Boolean(readOcrFormValue(host.form, 'is_schengen_unlimited')),
						}
					: {}),
				midni_validation_enabled: readOcrFormValue(host.form, 'midni_validation_enabled'),
				skip_pol_when_midni_authenticated: readOcrFormValue(
					host.form,
					'skip_pol_when_midni_authenticated',
				),
				eudi_wallet_enabled:
					environment.eudiWalletAllowed === true &&
					Boolean(readOcrFormValue(host.form, 'eudi_wallet_enabled')),
				skip_pol_when_eudi_authenticated: isOcrSelectFlagTrue(
					host.form,
					'skip_pol_when_eudi_authenticated',
				),
				fallback: buildOcrManualFallbackOrUndefined(
					readOcrSelectId(host.form, 'fallback'),
					host.company,
					readOcrSelectId(host.form, 'fallback_validation'),
					readOcrFormValue(host.form, 'fallback_emails'),
				),
			},
			uuid: readOcrFormValue(host.form, 'uuid'),
		} as IOcrOperation);
	}

	/**
	 * NIF-960: solo-EUDI (sin DNI/pasaporte/NIE) → `allowed_docs`/`not_allowed_docs` vacíos
	 * para que admin_back limpie DocumentRestriction stale.
	 */
	private buildOcrAllowedDocsPayload(host: WorkflowFormPayloadHost): {
		allowed_docs: IOcrAllowedDocs;
		not_allowed_docs: IOcrAllowedDocs;
	} {
		const hasPhysicalDocs =
			!!readOcrFormValue(host.form, 'dni_available') ||
			!!readOcrFormValue(host.form, 'passport_available') ||
			!!readOcrFormValue(host.form, 'nie_available');
		const eudiOnly =
			environment.eudiWalletAllowed === true &&
			Boolean(readOcrFormValue(host.form, 'eudi_wallet_enabled')) &&
			!hasPhysicalDocs;

		if (eudiOnly) {
			return { allowed_docs: {}, not_allowed_docs: {} };
		}

		return {
			allowed_docs: {
				passport: host.getAllowedDocs('passport') ?? undefined,
				dni: host.getAllowedDocs('dni') ?? undefined,
				nie: Boolean(readOcrFormValue(host.form, 'nie_available')),
			},
			not_allowed_docs: {
				passport: host.getNotAllowedDocs('passport') ?? undefined,
				dni: host.getNotAllowedDocs('dni') ?? undefined,
			},
		};
	}

	/**
	 * Bloque `config.obfuscation` (NIF-536). Solo se envía si el switch maestro está ON y hay algún field activo. `enabled` no se serializa.
	 */
	private buildOcrObfuscationPayload(host: WorkflowFormPayloadHost):
		| {
				fields: Record<string, boolean>;
				keep_unobfuscated_copy: boolean;
		  }
		| undefined {
		if (!Boolean(readOcrFormValue(host.form, 'obfuscation.enabled'))) {
			return undefined;
		}
		const selected = mapOcrMultiSelectIds(readOcrFormValue(host.form, 'obfuscation.fields'));
		const fields = ocrObfuscationFieldsFromSelected(selected);
		if (!Object.values(fields).some(Boolean)) {
			return undefined;
		}
		return {
			fields,
			keep_unobfuscated_copy: Boolean(readOcrFormValue(host.form, 'obfuscation.keep_unobfuscated_copy')),
		};
	}

	/**
	 * Bloque `config.fraud_scoring` del OCR (flags AEAT/AML/PEP + checks de manipulación).
	 * Abrir cuando: cambias antifraude OCR en el payload (no el resto de OCR).
	 */
	private buildOcrFraudScoringPayload(host: WorkflowFormPayloadHost): Record<string, unknown> {
		const legalAgeRaw = readOcrFormValue(host.form, 'fraud_scoring.legal_age');
		const checks = Object.fromEntries(
			OCR_FRAUD_CHECK_KEYS.map((checkKey) => [checkKey, this.buildOcrFraudCheckBlock(host, checkKey)]),
		);
		return {
			aeat: isOcrSelectFlagTrue(host.form, 'fraud_scoring.aeat'),
			legal_age: legalAgeRaw ? Number(legalAgeRaw) : null,
			expirated: readOcrFormValue(host.form, 'fraud_scoring.expirated'),
			valid_country: readOcrFormValue(host.form, 'fraud_scoring.valid_country'),
			aml: isOcrSelectFlagTrue(host.form, 'fraud_scoring.aml'),
			pep: isOcrSelectFlagTrue(host.form, 'fraud_scoring.pep'),
			...checks,
		};
	}

	/**
	 * Un check de fraude OCR (foto manipulada, fotocopia, etc.).
	 * Misma forma para todos; `undefined` si el check no está activo en UI (`availableCheckFraud`).
	 */
	private buildOcrFraudCheckBlock(
		host: WorkflowFormPayloadHost,
		checkKey: (typeof OCR_FRAUD_CHECK_KEYS)[number],
	): Record<string, unknown> | undefined {
		if (!host.availableCheckFraud(checkKey)) {
			return undefined;
		}
		const checkPath = `fraud_scoring.${checkKey}`;
		const emailAlertEnabled = Boolean(readOcrFormValue(host.form, `${checkPath}.email_alert_available`));
		return {
			blocking_step: readOcrFormValue(host.form, `${checkPath}.blocking_step`),
			ip_block: readOcrFormValue(host.form, `${checkPath}.ip_block`),
			identifier_block: readOcrFormValue(host.form, `${checkPath}.identifier_block`),
			email_alert: emailAlertEnabled
				? readOcrFormValue(host.form, `${checkPath}.email_alert`)
				: undefined,
		};
	}

	/** Si POL está disponible, usa `host.polSetting` ya sanitizado por el componente. */
	private appendPolOperationIfEnabled(host: WorkflowFormPayloadHost, operations: IOperation[]): void {
		if (!readOperationControlValue(host.form, OPERATION_TYPE.POL, 'available')) {
			return;
		}
		operations.push({
			type: OPERATION_TYPE.POL,
			config: {
				mode: readOperationControlValue(host.form, OPERATION_TYPE.POL, 'mode'),
				tutorial: readOperationControlValue(host.form, OPERATION_TYPE.POL, 'tutorial'),
				setting: host.polSetting,
				max_time: host.polSetting.max_time || 300,
				fraud_scoring: {
					facial_recognition: readOperationControlValue(
						host.form,
						OPERATION_TYPE.POL,
						'fraud_scoring.facial_recognition',
					),
					face_match: readOperationControlValue(host.form, OPERATION_TYPE.POL, 'fraud_scoring.face_match'),
					percentage_similarity: Number(
						readOperationControlValue(
							host.form,
							OPERATION_TYPE.POL,
							'fraud_scoring.percentage_similarity',
						),
					),
					anti_spoofing: readOperationControlValue(
						host.form,
						OPERATION_TYPE.POL,
						'fraud_scoring.anti_spoofing',
					),
				},
			},
			uuid: readOperationControlValue(host.form, OPERATION_TYPE.POL, 'uuid'),
		} as IPolOperation);
	}

	/**
	 * Poderes notariales: no es tipo API propio.
	 * - mode `auto` → empuja CONNECTIONS SCRIPTURE (con fallback manual solo si la compañía lo permite)
	 * - mode `manual` → empuja DOCUMENTS con un doc SCRIPTURE fijo
	 */
	private appendAttorneyPowersOperationIfEnabled(
		host: WorkflowFormPayloadHost,
		operations: IOperation[],
	): void {
		if (!readOperationControlValue(host.form, OPERATION_TYPE.ATTORNEY_POWERS, 'available')) {
			return;
		}
		const attorneyPowersFormValue = host.form.get(`operations.${OPERATION_TYPE.ATTORNEY_POWERS}`).value;
		const totalTries = Number(attorneyPowersFormValue.total_tries) || 2;
		const fallbackType =
			attorneyPowersFormValue.fallback?.id || attorneyPowersFormValue.fallback;
		const fallbackValidation =
			attorneyPowersFormValue.fallback_validation?.id || attorneyPowersFormValue.fallback_validation;
		if ((attorneyPowersFormValue.mode?.id || attorneyPowersFormValue.mode) === 'auto') {
			// Flujo automático: la escritura se valida vía conexión, no subiendo el PDF como DOCUMENTS.
			operations.push({
				type: OPERATION_TYPE.CONNECTIONS,
				config: {
					connection_type_uuid: this.masterService.connectionTypes.find(
						(connection: ISelect) => connection.id === CONNECTION_TYPE.SCRIPTURE,
					)?.value,
					type: CONNECTION_TYPE.SCRIPTURE,
					fallback: buildAttorneyManualFallbackOrUndefined(
						host.company?.manual_validation,
						fallbackType,
						fallbackValidation,
						attorneyPowersFormValue.fallback_emails,
					),
					mode: 'auto',
					evaluation: EVALUATION_TYPE.MATCH_DATA,
					total_tries: totalTries,
				},
				uuid: '',
			} as IConnectionOperation);
		} else {
			// Flujo manual: el usuario sube la escritura como documento SCRIPTURE (no conexión automática).
			operations.push({
				type: OPERATION_TYPE.DOCUMENTS,
				config: {
					tutorial: attorneyPowersFormValue.tutorial,
					title: '',
					docs: [
						{
							document_type_uuid: this.masterService.documentTypes.find(
								(connection: ISelect) => connection.id === CONNECTION_TYPE.SCRIPTURE,
							)?.value,
							required: true,
							method: 'both',
							total_tries: totalTries,
							min: 1,
							max: 1,
							instructions: '',
							instructions_photo: '',
							title: '',
							description: '',
							preview_validation: true,
							match_data: true,
						},
					],
				},
				uuid: attorneyPowersFormValue.uuid,
			} as IDocumentsOperation);
		}
	}

	/**
	 * DOCUMENTS y/o CONNECTIONS activos: reparte filas en
	 * - docs puros → {@link createDocs}
	 * - docs con conexión posible no elegidos en eval → {@link connectionsWithoutEval}
	 * - tipos de eval financiera → {@link createChooseOrConnection}
	 */
	private appendDocumentsAndConnectionsIfEnabled(
		host: WorkflowFormPayloadHost,
		operations: IOperation[],
	): void {
		if (
			!readOperationControlValue(host.form, OPERATION_TYPE.DOCUMENTS, 'available') &&
			!readOperationControlValue(host.form, OPERATION_TYPE.CONNECTIONS, 'available')
		) {
			return;
		}
		const docs: IDocumentsDocs[] = host.docsAvailables;
		const connections: CONNECTION_TYPE[] = host.connectionsAvailables;
		const { onlyDocs, connectionsWithoutEval } = partitionDocumentRowsForPayload(docs, connections);

		if (onlyDocs?.length) {
			this.createDocs(host, operations, onlyDocs);
		}
		if (connectionsWithoutEval?.length) {
			this.connectionsWithoutEval(host, operations, connectionsWithoutEval);
		}
		if (connections?.length) {
			this.createChooseOrConnection(host, operations, connections);
		}
	}

	/** Si FINISH está disponible, serializa loading_text y redirects/callbacks. */
	private appendFinishOperationIfEnabled(
		host: WorkflowFormPayloadHost,
		operations: IOperation[],
		allowedLocales: readonly string[],
	): void {
		if (!readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'available')) {
			return;
		}
		operations.push({
			type: OPERATION_TYPE.FINISH,
			config: {
				loading: readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'loading'),
				show_ko_reason: readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'show_ko_reason'),
				loading_text: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'loading_text'),
					allowedLocales,
				),
				ok_title: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'ok_title'),
					allowedLocales,
				),
				ok_description: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'ok_description'),
					allowedLocales,
				),
				ko_title: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'ko_title'),
					allowedLocales,
				),
				ko_description: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'ko_description'),
					allowedLocales,
				),
				cancelled_title: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'cancelled_title'),
					allowedLocales,
				),
				cancelled_description: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'cancelled_description'),
					allowedLocales,
				),
				pending_title: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'pending_title'),
					allowedLocales,
				),
				pending_description: serializeOptionalWorkflowLocaleForApi(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'pending_description'),
					allowedLocales,
				),
				redirect: readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'redirect'),
				redirect_ko: readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'redirect_ko'),
				redirect_cancelled: readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'redirect_cancelled'),
				callback: normalizeOptionalFinishUrl(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'callback'),
				),
				callback_ko: normalizeOptionalFinishUrl(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'callback_ko'),
				),
				callback_cancelled: normalizeOptionalFinishUrl(
					readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'callback_cancelled'),
				),
			},
			uuid: readOperationControlValue(host.form, OPERATION_TYPE.FINISH, 'uuid'),
		} as IFinishOperation);
	}

	/**
	 * Sección general del payload: alias, api_key, design + config (2FA, aviso diferido, antifraud…).
	 * Sin `operations` — el orquestador las añade aparte.
	 */
	private buildGeneralConfigSection(
		host: WorkflowFormPayloadHost,
		allowedLocales: readonly string[],
	): Omit<IWorkflowsCreatePayload, 'operations'> {
		const required_recording = host.form.get(`general.required_recording`)?.value;
		// Sin grabación obligatoria se respeta el toggle de estado guardado; si hay grabación obligatoria se fuerza a false.
		const has_saved_state = !required_recording
			? (host.form.get(`general.has_saved_state`).value?.id || host.form.get(`general.has_saved_state`).value) ===
				'true'
			: false;
		const delayed_process_warning = Boolean(host.form.get(`general.delayed_process_warning`).value);
		const delayedWarningVisibility = resolveSelectIdOrPlainValue<DelayedWarningVisibility>(
			host.form.get(`general.delayed_process_warning_visibility`)?.value,
			'start_and_resume',
		);
		const delayedWarningMessageType = resolveSelectIdOrPlainValue<DelayedWarningMessageType>(
			host.form.get(`general.delayed_process_warning_text_type`)?.value,
			'default',
		);
		const baseMetadata = host.form.get(`general.metadata`).value;
		const normalizedMetadata =
			baseMetadata && typeof baseMetadata === 'object' ? { ...baseMetadata } : ({} as Record<string, any>);
		delete normalizedMetadata.delayed_warning;
		normalizedMetadata.delayed_process_warning_visibility = delayed_process_warning
			? delayedWarningVisibility
			: null;

		const twoFactorChannel = this.getSelectedTwoFactorWorkflowChannel(host.form);

		// Texto del aviso diferido según tipo (custom vs default); metadata para persistir messageType.
		const delayed_process_warning_text = this.buildDelayedProcessWarningText(
			host.form,
			delayed_process_warning,
			delayedWarningMessageType,
			allowedLocales,
		);
		return {
			alias: host.form.get(`general.alias`).value,
			api_key: host.form.get(`general.api_key`).value?.id ?? host.form.get(`general.api_key`).value,
			config: {
				allowed_device: host.form
					.get(`general.allowed_device`)
					.value?.map((item: ISelect) => (item.id ? item.id : item)),
				version: 3,
				metadata: this.withTwoFactorMetadata(normalizedMetadata, twoFactorChannel),
				has_saved_state,
				required_recording,
				include_images: host.form.get(`general.include_images`).value,
				token_duration_minutes:
					host.form.get(`general.token_duration_minutes`)?.value?.id ??
					host.form.get(`general.token_duration_minutes`)?.value ??
					120,
				location: host.form.get(`general.location`).value ? 'enabled' : 'disabled',
				notify: host.form.get(`general.notify`).value,
				...this.buildWorkflowRetentionConfig(host),
				...(twoFactorChannel !== 'none'
					? {
							tfa: {
								type: twoFactorChannel as TwoFactorType,
							},
						}
					: {}),
				delayed_process_warning,
				delayed_process_warning_text,
				antifraud: {
					ip_block: host.form.get(`general.antifraud.ip_block`).value,
					identifier_block: host.form.get(`general.antifraud.identifier_block`).value,
					face_block: host.form.get(`general.antifraud.face_block`).value,
					block_operation: host.form.get(`general.antifraud.block_operation`).value,
					email_alert: host.form.get(`general.antifraud.email_alert`).value,
				},
				sdk: host.form.get(`general.sdk`).value,
				validation: {
					type: buildWorkflowValidationTypePayload(
						host.form,
						host.company?.manual_validation,
						host.getValidationErrorOptionIds(),
					),
					emails: buildWorkflowValidationEmailsPayload(host.form, host.company?.manual_validation),
				},
			},
			design_config_uuid: host.form.get(`general.theme`).value?.id ?? host.form.get(`general.theme`).value,
		};
	}

	/** Retención del profile: toggle UI `override_data_retention` no viaja al back; en alta omite nulls. */
	private buildWorkflowRetentionConfig(host: WorkflowFormPayloadHost): Record<string, RetentionDurationApiValue> {
		return buildRetentionApiFields(
			{
				document_retention_days: host.form.get('general.document_retention_days')?.value,
				media_retention_days: host.form.get('general.media_retention_days')?.value,
				info_retention_days: host.form.get('general.info_retention_days')?.value,
			},
			{
				overrideEnabled: !!host.form.get('general.override_data_retention')?.value,
				omitNullKeys: !host.id,
			},
		);
	}

	/**
	 * Garantiza el control `general.two_factor_channel` (workflows antiguos pueden no tenerlo).
	 */
	public ensureTwoFactorWorkflowControl(form: FormGroup): void {
		const generalGroup = form.get('general') as FormGroup | null;
		if (!generalGroup?.get('two_factor_channel')) {
			generalGroup?.addControl('two_factor_channel', new FormControl<AuthChannel>('none', Validators.required));
		}
	}

	/**
	 * Canal 2FA del form; valores distintos de sms/whatsapp → `'none'`.
	 */
	public getSelectedTwoFactorWorkflowChannel(form: FormGroup): AuthChannel {
		const channel = resolveSelectIdOrPlainValue<string>(
			form.get('general.two_factor_channel')?.value,
			'none',
		);
		if (channel === 'sms' || channel === 'whatsapp') {
			return channel;
		}
		return 'none';
	}

	/**
	 * Añade/sobrescribe `two_factor_channel` en metadata sin perder el resto de claves.
	 */
	public withTwoFactorMetadata(metadata: unknown, channel: AuthChannel): Record<string, unknown> {
		const nextMetadata =
			typeof metadata === 'object' && metadata !== null && !Array.isArray(metadata)
				? { ...(metadata as Record<string, unknown>) }
				: {};
		nextMetadata['two_factor_channel'] = channel;
		return nextMetadata;
	}

	/**
	 * Orden demo/PWCS: HOME primero, FINISH al final (vía {@link operationPriority}).
	 * NIF-657: si `FORM.config.autofill_from_ocr`, FORM va después de OCR/POL (priority 15).
	 */
	public orderOperationsForDemoFlow(operations: IOperation[]): IOperation[] {
		const autofillFromOcr = this.resolveFormAutofillFromOcr(operations);
		const weighted = [...operations].sort(
			(left, right) =>
				this.operationPriority(left.type, autofillFromOcr) -
				this.operationPriority(right.type, autofillFromOcr),
		);
		return weighted;
	}

	/**
	 * Prioridad numérica para ordenar ops en el payload (HOME=0, FORM=5|15, resto=10, FINISH=99).
	 * Con autofill: FORM=15 para que OCR/POL (10) precedan al formulario.
	 */
	public operationPriority(type: OPERATION_TYPE, autofillFromOcr = false): number {
		if (type === OPERATION_TYPE.HOME) {
			return 0;
		}
		if (type === OPERATION_TYPE.FORM) {
			return autofillFromOcr ? 15 : 5;
		}
		if (type === OPERATION_TYPE.FINISH) {
			return 99;
		}
		return 10;
	}

	/** Lee flag NIF-657 desde la op FORM ya montada en el payload. */
	private resolveFormAutofillFromOcr(operations: IOperation[]): boolean {
		const formOperation = operations.find((op) => op.type === OPERATION_TYPE.FORM) as
			| IFormOperation
			| undefined;
		return formOperation?.config?.autofill_from_ocr === true;
	}

	/**
	 * Texto del aviso de proceso diferido para el API.
	 * - Aviso off o tipo `default` → `null` (el back/front usan copy por defecto)
	 * - Tipo `custom` → mapa i18n serializado del form
	 */
	public buildDelayedProcessWarningText(
		form: FormGroup,
		delayedProcessWarning: boolean,
		delayedWarningMessageType: DelayedWarningMessageType,
		allowedLocales: readonly string[],
	): Record<string, string> | null {
		if (!delayedProcessWarning) {
			return null;
		}

		if (delayedWarningMessageType !== 'custom') {
			return null;
		}

		return serializeWorkflowLocaleFormValue(
			form.get(`general.delayed_process_warning_text`).value,
			allowedLocales,
		);
	}

	/**
	 * Empuja una operación DOCUMENTS con solo docs “puros” (sin connection_available).
	 * Omite `title` de la op si no hay título custom con texto en locales.
	 */
	public createDocs(host: WorkflowFormPayloadHost, operations: IOperation[], onlyDocs: IDocumentsDocs[]): void {
		const allowed = this.langSvc.getAllowedLanguages();
		const hasCustomTitle = host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.custom_title`).value;
		const opTitleSerialized = hasCustomTitle
			? serializeWorkflowLocaleFormValue(
					host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.title`).value,
					allowed,
				)
			: null;
		const includeOpTitle = hasCustomTitle && hasWorkflowLocaleText(opTitleSerialized);
		operations.push({
			type: OPERATION_TYPE.DOCUMENTS,
			config: {
				tutorial: host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.tutorial`).value,
				...(includeOpTitle ? { title: opTitleSerialized } : {}),
				docs: onlyDocs.map((doc: IDocumentsDocs) => {
					const instructionsPhotoSerialized =
						doc.type === 'GENERIC'
							? serializeWorkflowLocaleFormValue(doc.instructions_photo, allowed)
							: null;
					return {
						document_type_uuid: doc.docInfo?.uuid,
						required: doc.required,
						method: serializeDocumentMethodForApi(doc.method as Array<string | { id?: string }>),
						total_tries: normalizeOptionalDocNumber(doc.total_tries),
						min: normalizeOptionalDocNumber(doc.min),
						max: normalizeOptionalDocNumber(doc.max),
						instructions:
							doc.type === 'GENERIC' ? serializeWorkflowLocaleFormValue(doc.instructions, allowed) : '',
						...(doc.type === 'GENERIC' && hasWorkflowLocaleText(instructionsPhotoSerialized)
							? { instructions_photo: instructionsPhotoSerialized }
							: doc.type !== 'GENERIC'
								? { instructions_photo: '' }
								: {}),
						title: doc.type === 'GENERIC' ? serializeWorkflowLocaleFormValue(doc.title, allowed) : '',
						description:
							doc.type === 'GENERIC' ? serializeWorkflowLocaleFormValue(doc.description, allowed) : '',
						preview_validation: doc.preview_validation,
						match_data: normalizeDocsMatchData(doc.match_data),
					};
				}),
			},
			uuid: host.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.uuid`).value,
		} as IDocumentsOperation);
	}

	/**
	 * Docs que admiten conexión pero el usuario no los puso en evaluación financiera:
	 * se serializan como CONNECTIONS individuales sin `evaluation` (solo match/upload).
	 */
	public connectionsWithoutEval(host: WorkflowFormPayloadHost, operations: IOperation[], connectionsWithoutEval: IDocumentsDocs[]): void {
		if (connectionsWithoutEval.length) {
			connectionsWithoutEval.forEach((connection: IDocumentsDocs) => {
				operations.push({
					type: OPERATION_TYPE.CONNECTIONS,
					config: {
						connection_type_uuid: connection.docInfo?.confRecommended?.connection_uuid,
						type: connection.docInfo?.confRecommended?.connection_type,
						fallback: {
							type: 'manual',
							emails: '',
							validation: 'auto',
						},
						mode: 'auto',
						evaluation: null,
						total_tries: +host.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.total_tries`).value || 3,
					},
					uuid: '',
				} as IConnectionOperation);
			});
		}
	}

	/**
	 * Conexiones de evaluación financiera.
	 * Si hay más de una → envuelve en CHOOSE; si una sola → CONNECTIONS directo.
	 */
	public createChooseOrConnection(host: WorkflowFormPayloadHost, operations: IOperation[], connections: CONNECTION_TYPE[]): void {
		if (connections?.length) {
			// CHOOSE
			if (connections?.length > 1) {
				operations.push({
					type: OPERATION_TYPE.CHOOSE,
					config: {
						description: '',
						operations: connections.map(
							(connection: CONNECTION_TYPE) =>
								({
									type: OPERATION_TYPE.CONNECTIONS,
									config: {
										connection_type_uuid: this.masterService.connectionTypes.find(
											(connec: ISelect) => connec.id === connection,
										)?.value,
										type: connection,
										fallback: {
											type: 'manual',
											emails: '',
											validation: 'auto',
										},
										mode: 'auto',
										evaluation: EVALUATION_TYPE.SCORING,
										total_tries:
											+host.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.total_tries`)
												.value || 3,
									},
									uuid: '',
								}) as IConnectionOperation,
						),
					},
				} as IChooseOperation);
			}
			// CONNECTION
			if (connections?.length === 1) {
				operations.push({
					type: OPERATION_TYPE.CONNECTIONS,
					config: {
						connection_type_uuid: this.masterService.connectionTypes.find(
							(connection: ISelect) => connection.id === connections[0],
						)?.value,
						type: connections[0],
						fallback: {
							type: 'manual',
							emails: '',
							validation: 'auto',
						},
						mode: 'auto',
						evaluation:
							connections[0] === CONNECTION_TYPE.BANK_SCORING &&
							host.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.type`).value === 'verify'
								? EVALUATION_TYPE.MATCH_DATA
								: EVALUATION_TYPE.SCORING,
						match_name: true,
						total_tries: +host.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.total_tries`).value || 3,
					},
					uuid: '',
				} as IConnectionOperation);
			}
		}
	}

}
